export type FormatMoneyOptions = {
  showDollarSign?: boolean;
  showMagnitudeUnit?: boolean;
  showCommas?: boolean;
};

const defaultMoneyOptions: Required<FormatMoneyOptions> = {
  showDollarSign: true,
  showMagnitudeUnit: true,
  showCommas: true,
};

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

// Mirrors formatCompactCurrency in packages/ui/src/lib/formatters/number-formatters.ts.
// Duplicated here because this file is used by the worker which can't import @sfc/ui (no JSX).
const compactCurrencyFormatter = new Intl.NumberFormat("en", {
  style: "currency",
  currency: "USD",
  roundingPriority: "lessPrecision",
  maximumSignificantDigits: 3,
  maximumFractionDigits: 1,
  minimumFractionDigits: 0,
  notation: "compact",
} as Intl.NumberFormatOptions);

const compactNumberFormatter = new Intl.NumberFormat("en", {
  roundingPriority: "lessPrecision",
  maximumSignificantDigits: 3,
  maximumFractionDigits: 1,
  minimumFractionDigits: 0,
  notation: "compact",
} as Intl.NumberFormatOptions);

const percentFormatterCache: Record<number, Intl.NumberFormat> = {};

export function formatNumber(
  value: number,
  maximumFractionDigits?: number,
): string {
  return value.toLocaleString("en-US", {
    useGrouping: true,
    minimumFractionDigits: 0,
    maximumFractionDigits: maximumFractionDigits ?? (value < 100 ? 2 : 0),
  });
}

export function formatCurrency(value?: number): string {
  return value === undefined ? "" : currencyFormatter.format(value);
}

export function formatPercentage(
  value: number,
  maximumFractionDigits: number = 0,
): string {
  if (!percentFormatterCache[maximumFractionDigits]) {
    percentFormatterCache[maximumFractionDigits] = new Intl.NumberFormat(
      "en-US",
      {
        style: "percent",
        minimumFractionDigits: 0,
        maximumFractionDigits,
      },
    );
  }
  return percentFormatterCache[maximumFractionDigits].format(value);
}

export function formatMoney(
  amount: number,
  options?: FormatMoneyOptions,
): string {
  const showDollarSign =
    options?.showDollarSign ?? defaultMoneyOptions.showDollarSign;
  const showMagnitudeUnit =
    options?.showMagnitudeUnit ?? defaultMoneyOptions.showMagnitudeUnit;
  const showCommas = options?.showCommas ?? defaultMoneyOptions.showCommas;

  // Default: use Intl compact notation for proper K/M/B
  if (showMagnitudeUnit) {
    if (showDollarSign) {
      return compactCurrencyFormatter.format(amount);
    }
    return compactNumberFormatter.format(amount);
  }

  // showMagnitudeUnit: false — divide by 1000, no suffix (for graph axes)
  const isNegative = amount < 0;
  let formatted = Math.abs(amount / 1000)
    .toFixed(0)
    .toString();

  if (showCommas) {
    formatted = formatted.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }

  if (showDollarSign) {
    formatted = "$" + formatted;
  }

  if (isNegative) {
    formatted = "-" + formatted;
  }

  return formatted;
}

export function parseFormattedMoney(amount: string): number {
  if (amount.endsWith("K") || amount.endsWith("k")) {
    return 1000 * parseFormattedMoney(amount.substring(0, amount.length - 1));
  }
  if (amount.endsWith("M") || amount.endsWith("m")) {
    return 1e6 * parseFormattedMoney(amount.substring(0, amount.length - 1));
  }
  if (amount.endsWith("B") || amount.endsWith("b")) {
    return 1e9 * parseFormattedMoney(amount.substring(0, amount.length - 1));
  }

  const withoutDollarSignAndCommas = amount.replace(/[^\d.-]/g, "");
  return parseFloat(withoutDollarSignAndCommas);
}
